import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { AccountingDoc, AccountingService } from '../../services/accounting.service';
import { AuthService } from '../../services/auth.service';
import { DevicePermissionsService } from '../../services/device-permissions.service';
import {
  EMPTY_CAPS,
  NativeCamCaps,
  applyScanDefaults,
  buildScanConstraints,
  currentZoom,
  enrichFillLightModes,
  readNativeCaps,
  setTorch,
  setZoom,
  videoTrackOf,
} from './camera-native';
import {
  CaptureScore,
  DEFAULT_MARGINS,
  DocumentMargins,
  captureVideoFrame,
  detectDocumentMargins,
  lerpMargins,
  sanitizeMargins,
  scoreImageSource,
  selectShotsIntelligent,
  videoContainRect,
} from './document-capture-quality';

type DocHint = 'receipt' | 'purchase_invoice' | 'sales_invoice';
type Step = 'capture' | 'analyzing' | 'remediate' | 'result';

interface Shot {
  id: string;
  blob: Blob;
  url: string;
  score: number;
  ok: boolean;
  reason?: string;
  takenAt: number;
  selected: boolean;
}

const MAX_SHOTS = 12;
const MIN_M = 0;

/**
 * FAB flottant — capture multi-prises + sélection auto intelligente
 * (netteté/contraste), OCR via cursor-acp-shim model=auto.
 */
@Component({
  selector: 'vd-document-scan-bubble',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './document-scan-bubble.component.html',
  styleUrls: ['./document-scan-bubble.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentScanBubbleComponent implements OnInit, OnDestroy {
  @ViewChild('videoEl') videoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('camBox') camBoxRef?: ElementRef<HTMLElement>;

  private acc = inject(AccountingService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private devicePerms = inject(DevicePermissionsService);

  readonly visible = signal(false);
  readonly open = signal(false);
  readonly step = signal<Step>('capture');
  readonly docType = signal<DocHint>('receipt');
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);
  readonly result = signal<AccountingDoc | null>(null);
  readonly previewUrl = signal<string | null>(null);
  readonly reasonHint = signal<string | null>(null);

  readonly cameraOn = signal(false);
  readonly cameraError = signal<string | null>(null);
  readonly liveHint = signal<string | null>(null);
  readonly liveScore = signal<number | null>(null);
  readonly shots = signal<Shot[]>([]);
  /** Plusieurs documents distincts (sinon = meilleure prise seule). */
  readonly multiDocs = signal(false);
  readonly autoSelect = signal(true);
  /** Détection live des marges document + ajustement manuel. */
  readonly autoMargins = signal(true);
  readonly margins = signal<DocumentMargins>({ ...DEFAULT_MARGINS });
  readonly marginConf = signal(0);
  /** Bump quand videoWidth connu — force recalcul du calque contain. */
  readonly videoReady = signal(0);
  readonly importSummary = signal<string | null>(null);

  /** Capacités natives (torch / zoom / flip) — selon device + navigateur. */
  readonly camCaps = signal<NativeCamCaps>({ ...EMPTY_CAPS });
  readonly torchOn = signal(false);
  readonly zoomLevel = signal(1);
  readonly facingMode = signal<'environment' | 'user'>('environment');
  readonly camBusy = signal(false);

  formAmount: number | null = null;
  formTax: number | null = null;
  formDate = '';
  formSupplier = '';
  formCustomer = '';
  formRef = '';
  formCurrency = 'TND';

  private sub?: Subscription;
  private authTimer?: ReturnType<typeof setInterval>;
  private liveTimer?: ReturnType<typeof setInterval>;
  private mediaStream: MediaStream | null = null;
  private capturing = false;
  private dragKind: 'move' | 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se' | null = null;
  private dragStart: { x: number; y: number; m: DocumentMargins } | null = null;
  private manualUntil = 0;
  private detecting = false;

  ngOnInit(): void {
    this.refreshVisibility();
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.refreshVisibility();
        this.maybeAutoOpenFromQuery();
      });
    this.authTimer = setInterval(() => this.refreshVisibility(), 3000);
    // Shortcut PWA /comptabilite?scan=1
    setTimeout(() => this.maybeAutoOpenFromQuery(), 120);
  }

  /** Ouvre le scanner si `?scan=1` (raccourci PWA). */
  private maybeAutoOpenFromQuery(): void {
    try {
      const tree = this.router.parseUrl(this.router.url);
      const scan = tree.queryParams?.['scan'];
      if (scan !== '1' && scan !== 'true') return;
      if (!this.visible() || this.open()) return;
      this.open.set(true);
      this.resetFlow();
      void this.startCamera();
      delete tree.queryParams['scan'];
      void this.router.navigateByUrl(tree, { replaceUrl: true });
    } catch {
      /* ignore */
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.authTimer) clearInterval(this.authTimer);
    this.stopCamera();
    this.clearShots();
    this.revokePreview();
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.open()) this.close();
  }

  /** Routes publiques / hors contexte compta — jamais de FAB scan. */
  private static readonly HIDDEN_ROUTE_RE =
    /^\/(staff|login|Login|signup|CompleteSignUp|complete-signup|auth\/|social-checkout|public\/|tg\/app|pos\/terminal|agents\/(shift|terminal)|Welcom|legal)(\/|$|\?)/i;

  private refreshVisibility(): void {
    const path = (this.router.url || '').split('?')[0] || '';
    if (DocumentScanBubbleComponent.HIDDEN_ROUTE_RE.test(path)) {
      this.visible.set(false);
      if (this.open()) this.close();
      return;
    }
    const token = this.auth.getTokenFromCookies();
    if (!token) {
      this.visible.set(false);
      return;
    }
    const roles = this.auth.getRolesFromToken();
    const ok = roles.includes('vendor') || roles.includes('admin');
    this.visible.set(ok);
  }

  toggle(): void {
    if (this.open()) this.close();
    else {
      this.open.set(true);
      this.resetFlow();
      void this.startCamera();
    }
  }

  close(): void {
    this.open.set(false);
    this.busy.set(false);
    this.stopCamera();
    this.resetFlow();
  }

  private resetFlow(): void {
    this.step.set('capture');
    this.error.set(null);
    this.result.set(null);
    this.reasonHint.set(null);
    this.importSummary.set(null);
    this.formAmount = null;
    this.formTax = null;
    this.formDate = '';
    this.formSupplier = '';
    this.formCustomer = '';
    this.formRef = '';
    this.formCurrency = 'TND';
    this.clearShots();
    this.revokePreview();
    this.liveHint.set(null);
    this.liveScore.set(null);
    this.margins.set({ ...DEFAULT_MARGINS });
    this.marginConf.set(0);
    this.autoMargins.set(true);
    this.manualUntil = 0;
    this.dragKind = null;
    this.dragStart = null;
    this.torchOn.set(false);
    this.zoomLevel.set(1);
    this.camCaps.set({ ...EMPTY_CAPS });
    this.facingMode.set('environment');
  }

  setType(t: DocHint): void {
    this.docType.set(t);
  }

  toggleMulti(): void {
    this.multiDocs.update((v) => !v);
    this.applyAutoSelect();
  }

  toggleAuto(): void {
    this.autoSelect.update((v) => !v);
    if (this.autoSelect()) this.applyAutoSelect();
  }

  toggleAutoMargins(): void {
    this.autoMargins.update((v) => !v);
    if (this.autoMargins()) {
      this.manualUntil = 0;
      void this.tickLiveScore();
    }
  }

  resetMargins(): void {
    this.margins.set({ ...DEFAULT_MARGINS });
    this.manualUntil = 0;
    if (this.autoMargins()) void this.tickLiveScore();
  }

  /** Zone vidéo réelle (object-fit: contain) dans la boîte caméra. */
  private contentRect(): { left: number; top: number; width: number; height: number } | null {
    const box = this.camBoxRef?.nativeElement?.getBoundingClientRect();
    const v = this.videoRef?.nativeElement;
    if (!box || !v?.videoWidth || !v.videoHeight || box.width < 8) return null;
    return videoContainRect(v.videoWidth, v.videoHeight, box.width, box.height);
  }

  /** Positionne le masque pile sur la zone vidéo affichée (pas les bandes letterbox). */
  videoLayerStyle(): Record<string, string> {
    const box = this.camBoxRef?.nativeElement?.getBoundingClientRect();
    const r = this.contentRect();
    if (!box || !r) return { left: '0', top: '0', width: '100%', height: '100%' };
    return {
      left: `${(r.left / box.width) * 100}%`,
      top: `${(r.top / box.height) * 100}%`,
      width: `${(r.width / box.width) * 100}%`,
      height: `${(r.height / box.height) * 100}%`,
    };
  }

  /** Zone utile en % relatifs à la couche vidéo. */
  cropStyle(): Record<string, string> {
    const m = this.margins();
    return {
      left: `${m.left * 100}%`,
      top: `${m.top * 100}%`,
      width: `${(1 - m.left - m.right) * 100}%`,
      height: `${(1 - m.top - m.bottom) * 100}%`,
    };
  }

  onMarginPointerDown(kind: NonNullable<typeof this.dragKind>, ev: PointerEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    const el = ev.currentTarget as HTMLElement;
    el.setPointerCapture?.(ev.pointerId);
    this.dragKind = kind;
    this.dragStart = { x: ev.clientX, y: ev.clientY, m: { ...this.margins() } };
    this.manualUntil = Date.now() + 6000;
  }

  @HostListener('document:pointermove', ['$event'])
  onMarginPointerMove(ev: PointerEvent): void {
    if (!this.dragKind || !this.dragStart) return;
    const content = this.contentRect();
    if (!content || content.width < 8 || content.height < 8) return;
    const dx = (ev.clientX - this.dragStart.x) / content.width;
    const dy = (ev.clientY - this.dragStart.y) / content.height;
    const base = this.dragStart.m;
    let next: DocumentMargins = { ...base };
    const k = this.dragKind;
    if (k === 'move') {
      const w = 1 - base.left - base.right;
      const h = 1 - base.top - base.bottom;
      let left = base.left + dx;
      let top = base.top + dy;
      left = Math.max(MIN_M, Math.min(1 - w - MIN_M, left));
      top = Math.max(MIN_M, Math.min(1 - h - MIN_M, top));
      next = { ...base, left, top, right: 1 - left - w, bottom: 1 - top - h };
    } else {
      if (k.includes('w') || k === 'w') next.left = base.left + dx;
      if (k.includes('e') || k === 'e') next.right = base.right - dx;
      if (k.includes('n') || k === 'n') next.top = base.top + dy;
      if (k.includes('s') || k === 's') next.bottom = base.bottom - dy;
    }
    this.margins.set(sanitizeMargins(next));
    this.manualUntil = Date.now() + 6000;
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  onMarginPointerUp(): void {
    this.dragKind = null;
    this.dragStart = null;
  }

  async startCamera(): Promise<void> {
    this.cameraError.set(null);
    this.stopCamera(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      this.cameraError.set('Caméra non supportée — utilisez l’import fichier.');
      return;
    }
    if (!window.isSecureContext) {
      this.cameraError.set('Caméra / torche nécessitent HTTPS (ou PWA installée).');
      return;
    }
    this.camBusy.set(true);
    try {
      const facing = this.facingMode();
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia(
          buildScanConstraints(facing),
        );
      } catch {
        // Fallback sans facingMode strict (desktop / WebView restrictif)
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: true,
        });
      }
      this.cameraOn.set(true);
      await this.bootstrapNativeTrack();
      void this.devicePerms.acquireWakeLock();
      // Attendre le rendu ViewChild
      setTimeout(() => {
        const v = this.videoRef?.nativeElement;
        if (v && this.mediaStream) {
          v.srcObject = this.mediaStream;
          const markReady = () => this.videoReady.update((n) => n + 1);
          v.onloadedmetadata = markReady;
          v.onresize = markReady;
          void v.play().then(markReady).catch(() => undefined);
        }
        this.startLiveMeter();
      }, 80);
    } catch {
      this.cameraOn.set(false);
      this.cameraError.set(
        'Accès caméra refusé — autorisez la caméra (Réglages PWA / navigateur) ou importez un fichier.',
      );
    } finally {
      this.camBusy.set(false);
    }
  }

  /** Stop tracks ; `releaseWake=true` libère aussi le wake-lock écran. */
  stopCamera(releaseWake = true): void {
    this.stopLiveMeter();
    const track = videoTrackOf(this.mediaStream);
    if (track && this.torchOn()) {
      void setTorch(track, false);
    }
    this.torchOn.set(false);
    if (this.mediaStream) {
      for (const t of this.mediaStream.getTracks()) t.stop();
      this.mediaStream = null;
    }
    const v = this.videoRef?.nativeElement;
    if (v) v.srcObject = null;
    this.cameraOn.set(false);
    this.camCaps.set({ ...EMPTY_CAPS });
    if (releaseWake) this.devicePerms.releaseWakeLock();
  }

  private async bootstrapNativeTrack(): Promise<void> {
    const track = videoTrackOf(this.mediaStream);
    if (!track) return;
    await applyScanDefaults(track);
    let caps = readNativeCaps(track);
    caps = await enrichFillLightModes(track, caps);
    this.camCaps.set(caps);
    this.zoomLevel.set(currentZoom(track));
    // Restaurer torche si on flip/redémarre et qu’elle était demandée
    if (this.torchOn() && caps.torch) {
      const ok = await setTorch(track, true);
      if (!ok) this.torchOn.set(false);
    } else {
      this.torchOn.set(false);
    }
  }

  async toggleTorch(): Promise<void> {
    const track = videoTrackOf(this.mediaStream);
    if (!track || !this.camCaps().torch || this.camBusy()) return;
    const next = !this.torchOn();
    this.camBusy.set(true);
    try {
      const ok = await setTorch(track, next);
      this.torchOn.set(ok ? next : false);
      if (!ok) this.liveHint.set('Torche non disponible sur cet appareil');
    } finally {
      this.camBusy.set(false);
    }
  }

  async nudgeZoom(dir: 1 | -1): Promise<void> {
    const caps = this.camCaps();
    const track = videoTrackOf(this.mediaStream);
    if (!track || !caps.zoom) return;
    const step = Math.max(caps.zoomStep || 0.1, (caps.zoomMax - caps.zoomMin) / 8);
    const next = Math.max(
      caps.zoomMin,
      Math.min(caps.zoomMax, this.zoomLevel() + dir * step),
    );
    if (await setZoom(track, next)) this.zoomLevel.set(currentZoom(track) || next);
  }

  async onZoomInput(ev: Event): Promise<void> {
    const track = videoTrackOf(this.mediaStream);
    const caps = this.camCaps();
    if (!track || !caps.zoom) return;
    const raw = Number((ev.target as HTMLInputElement).value);
    if (!Number.isFinite(raw)) return;
    if (await setZoom(track, raw)) this.zoomLevel.set(currentZoom(track) || raw);
  }

  async flipCamera(): Promise<void> {
    if (this.camBusy()) return;
    const next = this.facingMode() === 'environment' ? 'user' : 'environment';
    this.facingMode.set(next);
    // Torche uniquement pertinente en arrière
    if (next === 'user') this.torchOn.set(false);
    await this.startCamera();
  }

  private startLiveMeter(): void {
    this.stopLiveMeter();
    this.liveTimer = setInterval(() => void this.tickLiveScore(), 480);
  }

  private stopLiveMeter(): void {
    if (this.liveTimer) {
      clearInterval(this.liveTimer);
      this.liveTimer = undefined;
    }
  }

  private async tickLiveScore(): Promise<void> {
    const v = this.videoRef?.nativeElement;
    if (!v || !this.cameraOn() || this.busy() || this.step() !== 'capture') return;
    if (!v.videoWidth || this.detecting) return;
    this.detecting = true;
    try {
      const s = await scoreImageSource(v);
      this.liveScore.set(s.score);

      const allowAuto =
        this.autoMargins() && Date.now() >= this.manualUntil && !this.dragKind;
      if (allowAuto) {
        const detected = await detectDocumentMargins(v);
        this.marginConf.set(detected.confidence);
        // N’appliquer l’auto que si la détection est crédible ; sinon garder le cadre.
        if (detected.confidence >= 0.28) {
          const t = detected.confidence >= 0.55 ? 0.55 : 0.32;
          const blended = lerpMargins(this.margins(), detected, t);
          this.margins.set(blended);
          this.liveHint.set(this.liveAdvice(s, blended));
        } else {
          this.liveHint.set(this.liveAdvice(s, { ...this.margins(), confidence: detected.confidence }));
        }
      } else {
        this.liveHint.set(this.liveAdvice(s, this.margins()));
      }
    } catch {
      /* ignore */
    } finally {
      this.detecting = false;
    }
  }

  private liveAdvice(s: CaptureScore, m: DocumentMargins): string {
    if (this.dragKind || Date.now() < this.manualUntil) {
      return 'Marges ajustées — relâchez pour reprendre l’auto';
    }
    if (s.reason === 'blurry') return 'Stabilisez — image floue';
    if (s.reason === 'dark') return 'Éclairage insuffisant';
    if (s.reason === 'bright') return 'Trop de lumière / reflet';
    if (s.reason === 'low_contrast') return 'Contraste faible — fond uni';
    if (m.confidence >= 0.55 && s.score >= 70) return 'Document cadré — prêt à capturer';
    if (m.confidence < 0.35) return 'Cherche les bords — placez le doc à plat';
    if (s.score >= 70) return 'Bonne prise — vérifiez les marges';
    if (s.score >= 45) return 'Correct — ajustez les coins si besoin';
    return 'Cadrez le document à plat';
  }

  async shutter(): Promise<void> {
    if (this.capturing || this.busy()) return;
    const v = this.videoRef?.nativeElement;
    if (!v || !this.cameraOn()) {
      this.error.set('Caméra inactive.');
      return;
    }
    if (this.shots().length >= MAX_SHOTS) {
      this.error.set(`Maximum ${MAX_SHOTS} prises — analysez ou retirez-en.`);
      return;
    }
    this.capturing = true;
    this.error.set(null);
    try {
      const blob = await captureVideoFrame(v, 0.92, this.margins());
      const scored = await scoreImageSource(blob);
      const shot: Shot = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        blob,
        url: URL.createObjectURL(blob),
        score: scored.score,
        ok: scored.ok,
        reason: scored.reason,
        takenAt: Date.now(),
        selected: false,
      };
      this.shots.update((list) => [...list, shot]);
      this.applyAutoSelect();
    } catch (e: any) {
      this.error.set(e?.message || 'Capture impossible');
    } finally {
      this.capturing = false;
    }
  }

  private applyAutoSelect(): void {
    if (!this.autoSelect()) return;
    const picked = new Set(
      selectShotsIntelligent(this.shots(), {
        multi: this.multiDocs(),
        minScore: 35,
        maxKeep: this.multiDocs() ? 10 : 1,
      }).map((s) => s.id),
    );
    this.shots.update((list) =>
      list.map((s) => ({ ...s, selected: picked.has(s.id) })),
    );
  }

  toggleShot(id: string): void {
    if (this.autoSelect()) this.autoSelect.set(false);
    this.shots.update((list) =>
      list.map((s) => {
        if (s.id !== id) {
          // en mode single, une seule sélection
          if (!this.multiDocs() && s.selected) return { ...s, selected: false };
          return s;
        }
        return { ...s, selected: !s.selected };
      }),
    );
  }

  removeShot(id: string): void {
    const victim = this.shots().find((s) => s.id === id);
    if (victim) URL.revokeObjectURL(victim.url);
    this.shots.update((list) => list.filter((s) => s.id !== id));
    if (this.autoSelect()) this.applyAutoSelect();
  }

  clearShots(): void {
    for (const s of this.shots()) URL.revokeObjectURL(s.url);
    this.shots.set([]);
  }

  selectedShots(): Shot[] {
    return this.shots().filter((s) => s.selected);
  }

  analyzeSelected(): void {
    const picked = this.selectedShots();
    if (!picked.length) {
      this.error.set('Sélectionnez au moins une prise (ou activez la sélection auto).');
      return;
    }
    // Préférer les prises OK ; sinon la meilleure score
    const ordered = [...picked].sort((a, b) => b.score - a.score);
    if (this.multiDocs() && ordered.length > 1) {
      this.importMany(ordered);
    } else {
      this.scanBlob(ordered[0].blob, ordered[0].url);
    }
  }

  onFile(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (!files.length) return;

    const pdfs = files.filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
    );
    const images = files.filter((f) => f.type.startsWith('image/'));

    if (pdfs.length === 1 && !images.length) {
      this.scan(pdfs[0]);
      return;
    }
    if (pdfs.length + images.length > 1 || this.multiDocs()) {
      this.importFiles([...images, ...pdfs]);
      return;
    }
    if (images[0]) {
      void this.addFileAsShot(images[0]).then(() => this.analyzeSelected());
      return;
    }
    this.error.set('Format non supporté (JPEG/PNG/WebP/PDF).');
  }

  private async addFileAsShot(file: File): Promise<void> {
    const scored = await scoreImageSource(file);
    const shot: Shot = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      blob: file,
      url: URL.createObjectURL(file),
      score: scored.score,
      ok: scored.ok,
      reason: scored.reason,
      takenAt: Date.now(),
      selected: true,
    };
    this.shots.update((list) => [...list, shot]);
    this.applyAutoSelect();
  }

  private scan(file: File): void {
    this.busy.set(true);
    this.error.set(null);
    this.result.set(null);
    this.reasonHint.set(null);
    this.importSummary.set(null);
    this.step.set('analyzing');
    this.stopCamera();
    this.revokePreview();
    if (file.type.startsWith('image/')) {
      this.previewUrl.set(URL.createObjectURL(file));
    }
    this.acc.scanDocument(file, this.docType()).subscribe({
      next: (doc) => this.onScanOk(doc),
      error: (e) => this.onScanFail(e),
    });
  }

  private scanBlob(blob: Blob, preview?: string): void {
    const file = new File([blob], `scan-${Date.now()}.jpg`, { type: 'image/jpeg' });
    this.busy.set(true);
    this.error.set(null);
    this.result.set(null);
    this.reasonHint.set(null);
    this.step.set('analyzing');
    this.stopCamera();
    this.revokePreview();
    if (preview) this.previewUrl.set(preview);
    else if (blob.type.startsWith('image/')) {
      this.previewUrl.set(URL.createObjectURL(blob));
    }
    this.acc.scanDocument(file, this.docType()).subscribe({
      next: (doc) => this.onScanOk(doc),
      error: (e) => this.onScanFail(e),
    });
  }

  private importMany(shots: Shot[]): void {
    const files = shots.map(
      (s, i) => new File([s.blob], `facture-${i + 1}-${s.score}.jpg`, { type: 'image/jpeg' }),
    );
    this.importFiles(files);
  }

  private importFiles(files: File[]): void {
    if (!files.length) return;
    this.busy.set(true);
    this.error.set(null);
    this.result.set(null);
    this.importSummary.set(null);
    this.step.set('analyzing');
    this.stopCamera();
    this.acc.importDocuments(files, this.docType() === 'receipt' ? 'purchase_invoice' : this.docType()).subscribe({
      next: (r) => {
        this.busy.set(false);
        this.importSummary.set(`${r.ok || 0} importée(s)${r.failed ? ` · ${r.failed} refusée(s)` : ''}`);
        const docs = r.documents || [];
        if (!docs.length) {
          this.step.set('capture');
          this.error.set(
            r.errors?.[0]?.detail || r.errors?.[0]?.error || 'Aucune facture importée.',
          );
          void this.startCamera();
          return;
        }
        const first = docs[0];
        this.result.set(first);
        this.fillFormFrom(first);
        if (this.needsRemediation(first)) {
          this.reasonHint.set(this.reasonLabel(first.reason));
          this.step.set('remediate');
        } else {
          this.step.set('result');
        }
        if (r.errors?.length) {
          this.error.set(
            r.errors.slice(0, 2).map((e) => e.detail || e.error).join(' ; '),
          );
        }
      },
      error: (e) => this.onScanFail(e),
    });
  }

  private onScanOk(doc: AccountingDoc): void {
    this.busy.set(false);
    this.result.set(doc);
    this.fillFormFrom(doc);
    if (this.needsRemediation(doc)) {
      this.reasonHint.set(this.reasonLabel(doc.reason));
      this.step.set('remediate');
    } else {
      this.step.set('result');
    }
  }

  private onScanFail(e: any): void {
    this.busy.set(false);
    this.step.set('capture');
    this.error.set(
      e?.error?.detail || 'Scan impossible. Réessayez avec une photo nette.',
    );
    void this.startCamera();
  }

  private needsRemediation(doc: AccountingDoc): boolean {
    if (doc.needs_remediation) return true;
    if (doc.amount_ttc == null || Number(doc.amount_ttc) <= 0) return true;
    const r = doc.reason || '';
    return [
      'blurry', 'low_contrast', 'too_small', 'low_confidence',
      'amount_missing', 'partial', 'parse_error', 'ai_unavailable',
      'not_image', 'corrupt', 'provider_quota', 'insufficient_credits',
    ].includes(r);
  }

  private fillFormFrom(doc: AccountingDoc): void {
    this.formAmount = doc.amount_ttc ?? null;
    this.formTax = doc.tax_amount ?? null;
    this.formDate = doc.doc_date || '';
    this.formSupplier = doc.supplier_name || '';
    this.formCustomer = doc.customer_name || '';
    this.formRef = doc.reference || '';
    this.formCurrency = doc.currency || 'TND';
  }

  reasonLabel(reason?: string | null): string {
    const map: Record<string, string> = {
      blurry: 'Image floue — reprenez la photo ou saisissez les montants.',
      low_contrast: 'Contraste insuffisant — éclairage trop faible ou trop fort.',
      too_small: 'Image trop petite — photographiez plus près du document.',
      low_confidence: 'Lecture incertaine — vérifiez ou corrigez les champs.',
      amount_missing: 'Montant TTC non lu — saisie manuelle requise.',
      partial: 'Extraction partielle — complètez les champs manquants.',
      parse_error: 'Réponse IA illisible — saisie manuelle.',
      ai_unavailable: 'Agent IA indisponible — saisie manuelle possible.',
      not_image: 'Fichier non image — envoyez une photo JPEG/PNG/WebP.',
      corrupt: 'Fichier image corrompu.',
      provider_quota: 'Quota Cursor épuisé — saisie manuelle ou réessayez plus tard.',
      insufficient_credits: 'Crédits IA insuffisants — saisie manuelle possible.',
      workspace_write_failed: 'Impossible d’écrire dans le workspace agent.',
    };
    return map[reason || ''] || 'Vérifiez les données extraites avant de continuer.';
  }

  retake(): void {
    this.result.set(null);
    this.reasonHint.set(null);
    this.error.set(null);
    this.importSummary.set(null);
    this.step.set('capture');
    this.revokePreview();
    void this.startCamera();
  }

  saveRemediation(): void {
    const d = this.result();
    if (!d || this.busy()) return;
    if (this.formAmount == null || Number(this.formAmount) <= 0) {
      this.error.set('Le montant TTC est obligatoire.');
      return;
    }
    this.busy.set(true);
    this.error.set(null);
    this.acc.patchDocument(d.id, {
      amount_ttc: Number(this.formAmount),
      tax_amount: this.formTax != null ? Number(this.formTax) : 0,
      doc_date: this.formDate || null,
      supplier_name: this.formSupplier || null,
      customer_name: this.formCustomer || null,
      reference: this.formRef || null,
      currency: this.formCurrency || 'TND',
      doc_type: this.docType(),
    }).subscribe({
      next: (doc) => {
        this.busy.set(false);
        this.result.set(doc);
        this.step.set('result');
      },
      error: (e) => {
        this.busy.set(false);
        this.error.set(e?.error?.detail || 'Enregistrement impossible.');
      },
    });
  }

  propose(): void {
    const d = this.result();
    if (!d || this.busy()) return;
    if (d.amount_ttc == null || Number(d.amount_ttc) <= 0) {
      this.step.set('remediate');
      this.reasonHint.set(this.reasonLabel('amount_missing'));
      return;
    }
    this.busy.set(true);
    this.acc.proposeEntry(d.id).subscribe({
      next: (doc) => {
        this.busy.set(false);
        if ((doc as any)?.error) {
          this.error.set((doc as any).detail || 'Proposition impossible.');
          if ((doc as any).needs_remediation) this.step.set('remediate');
          return;
        }
        this.result.set(doc);
        this.step.set('result');
      },
      error: (e) => {
        this.busy.set(false);
        const detail = e?.error?.detail || e?.error?.error || 'Proposition d’écriture impossible.';
        this.error.set(typeof detail === 'string' ? detail : 'Proposition impossible.');
        if (e?.error?.needs_remediation || e?.error?.error === 'amount_missing') {
          this.step.set('remediate');
        }
      },
    });
  }

  reconcile(match?: { kind: string; id: number }): void {
    const d = this.result();
    if (!d || this.busy()) return;
    this.busy.set(true);
    const payload: any = { post: true, match_method: match ? 'heuristic' : 'manual' };
    if (match?.kind === 'expense') payload.expense_id = match.id;
    if (match?.kind === 'order') payload.order_id = match.id;
    this.acc.reconcileDocument(d.id, payload).subscribe({
      next: (doc) => {
        this.busy.set(false);
        this.result.set(doc);
      },
      error: (e) => {
        this.busy.set(false);
        this.error.set(e?.error?.detail || 'Rapprochement impossible.');
      },
    });
  }

  dismiss(): void {
    const d = this.result();
    if (!d) return;
    this.acc.dismissDocument(d.id).subscribe({
      next: () => this.close(),
      error: () => this.close(),
    });
  }

  goCompta(): void {
    this.close();
    void this.router.navigateByUrl('/comptabilite');
  }

  private revokePreview(): void {
    const u = this.previewUrl();
    if (u && !this.shots().some((s) => s.url === u)) URL.revokeObjectURL(u);
    this.previewUrl.set(null);
  }

  typeLabel(t: string): string {
    return ({
      receipt: 'Reçu',
      purchase_invoice: 'Facture fournisseur',
      sales_invoice: 'Facture client',
    } as Record<string, string>)[t] || t;
  }

  scoreClass(score: number): string {
    if (score >= 70) return 'good';
    if (score >= 45) return 'mid';
    return 'bad';
  }
}
